function pad2(n) {
  return String(n).padStart(2, '0');
}

function formatYMD(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function parseYMD(s) {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d, 12, 0, 0, 0);
}

function eachDayBetween(startStr, endStr) {
  const out = [];
  const cur = parseYMD(startStr);
  const end = parseYMD(endStr);
  while (cur <= end) {
    out.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

/**
 * Merge API by_date into a continuous calendar range (UTC calendar days as YYYY-MM-DD keys).
 * @param {{ date: string, count: number }[]} byDate
 */
export function padUsageByDate(byDate, dateFrom, dateTo) {
  const m = new Map((byDate || []).map((d) => [d.date, d.count ?? 0]));
  const days = eachDayBetween(dateFrom, dateTo);
  return days.map((d) => {
    const key = formatYMD(d);
    return {
      day: `${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`,
      fullDate: key,
      insights: m.get(key) ?? 0,
    };
  });
}

/**
 * @param {{ date: string, users?: {
 *   employee_id: string | null,
 *   first_name: string | null,
 *   last_name: string | null,
 *   count: number,
 * }[] }[]} byDate
 * @returns {{ key: string, name: string, value: number }[]}
 */
export function aggregateUsageUsers(byDate) {
  const tally = new Map();
  for (const row of byDate || []) {
    for (const u of row.users || []) {
      const employeeId = u.employee_id == null ? null : String(u.employee_id);
      const key = employeeId == null ? '__null__' : `employee:${employeeId}`;
      const firstName = typeof u.first_name === 'string' ? u.first_name.trim() : '';
      const lastName = typeof u.last_name === 'string' ? u.last_name.trim() : '';
      const displayName = [firstName, lastName].filter(Boolean).join(' ');
      const label = displayName || employeeId || 'Unknown';
      const labelRank = displayName ? (firstName && lastName ? 3 : 2) : employeeId ? 1 : 0;
      const existing = tally.get(key);

      if (existing) {
        existing.value += u.count ?? 0;
        if (labelRank > existing.labelRank) {
          existing.name = label;
          existing.labelRank = labelRank;
        }
      } else {
        tally.set(key, { key, name: label, value: u.count ?? 0, labelRank });
      }
    }
  }
  return [...tally.values()]
    .map(({ labelRank, ...entry }) => entry)
    .sort((a, b) => b.value - a.value || a.name.localeCompare(b.name));
}

/** Number of distinct customers shown as individual bars before the rest roll into "Other". */
export const CUSTOMER_CHART_TOP_N = 12;

/**
 * @param {{ date: string, customers?: { customer_name: string, count: number }[] }[]} byDate
 * @returns {{
 *   chartData: { name: string, value: number }[],
 *   remainder: { name: string, value: number }[],
 * }}
 */
export function aggregateUsageCustomers(byDate) {
  const tally = new Map();
  for (const row of byDate || []) {
    for (const c of row.customers || []) {
      const name = c.customer_name ?? 'unknown';
      tally.set(name, (tally.get(name) || 0) + (c.count ?? 0));
    }
  }
  const arr = [...tally.entries()]
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
  if (arr.length <= CUSTOMER_CHART_TOP_N) {
    return { chartData: arr, remainder: [] };
  }
  const top = arr.slice(0, CUSTOMER_CHART_TOP_N);
  const remainder = arr.slice(CUSTOMER_CHART_TOP_N);
  const otherSum = remainder.reduce((s, x) => s + x.value, 0);
  return {
    chartData: [...top, { name: 'Other', value: otherSum }],
    remainder,
  };
}
